// src/pages/AdminVerifactu.js
// =========================================================================
// VERI*FACTU DE LA CLÍNICA (Facturación > VeriFactu, solo administrador)
//
// Asistente para empezar a enviar las facturas a Hacienda a través de
// Verifacti: datos, autorización, factura de prueba, documento de
// representación (solo en producción) y activar. Todo lo comprueba también
// el backend (src/verifactu); aquí solo se guía.
// =========================================================================
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import { Modal } from '../components/common/ui';
import TextoLegal from '../components/legal/TextoLegal';

const URL_PLANES = 'https://klyndu.com/contacto?tipo=precios';

const fecha = (iso) => (iso ? new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }) : '');

const inputBase = 'w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:border-clinica-primary focus:ring-clinica-primary/15';
const botonPrincipal = 'inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-bold text-white bg-slate-900 hover:bg-slate-800 rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed';
const botonSecundario = 'inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed';

const Aviso = ({ tono = 'sky', children }) => {
    const tonos = {
        sky: 'bg-sky-50 border-sky-200 text-sky-900',
        amber: 'bg-amber-50 border-amber-200 text-amber-900',
        rose: 'bg-rose-50 border-rose-200 text-rose-900',
        emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900'
    };
    return <div className={`p-4 rounded-2xl border text-sm font-semibold leading-relaxed ${tonos[tono]}`}>{children}</div>;
};

const Paso = ({ numero, titulo, hecho, bloqueado, children }) => (
    <section className={`bg-white border border-slate-200 rounded-[2rem] p-6 md:p-8 shadow-sm ${bloqueado ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="flex items-center gap-3 mb-4">
            <span className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-black ${hecho ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                {hecho ? '✓' : numero}
            </span>
            <h2 className="text-lg font-black text-slate-800">{titulo}</h2>
        </div>
        <div className="space-y-4">{children}</div>
    </section>
);

const Opcion = ({ nombre, valor, actual, onChange, titulo, detalle }) => (
    <label aria-label={titulo} className={`flex items-start gap-3 p-4 border rounded-2xl cursor-pointer transition-colors ${actual === valor ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:bg-slate-50'}`}>
        <input type="radio" name={nombre} value={valor} checked={actual === valor} onChange={() => onChange(valor)} className="mt-1" />
        <span>
            <span className="block text-sm font-black text-slate-800">{titulo}</span>
            <span className="block text-xs font-semibold text-slate-500">{detalle}</span>
        </span>
    </label>
);

const REPRESENTACION_VACIA = {
    tipo_entidad: 'juridica', nombre_entidad: '', municipio_entidad: '', calle_entidad: '', numero_entidad: '',
    nombre_representante: '', nif_representante: '', municipio_representante: '', calle_representante: '', numero_representante: ''
};

const AdminVerifactu = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [estado, setEstado] = useState(null);
    const [datos, setDatos] = useState({ tipoContribuyente: '', territorio: '' });
    const [ocupado, setOcupado] = useState('');
    const [prueba, setPrueba] = useState(null);
    const [rep, setRep] = useState(REPRESENTACION_VACIA);
    const [entendido, setEntendido] = useState(false);
    const [aceptoAutorizacion, setAceptoAutorizacion] = useState(false);
    const [verAutorizacion, setVerAutorizacion] = useState(false);

    const aplicar = useCallback((e) => {
        setEstado(e);
        setDatos({ tipoContribuyente: e.tipoContribuyente || '', territorio: e.territorio || '' });
    }, []);

    const cargar = useCallback(async () => {
        try {
            aplicar(await api.get('/verifactu'));
        } catch (err) {
            showToast(err.message || 'No se pudo cargar VeriFactu.', 'error');
        }
    }, [aplicar, showToast]);

    useEffect(() => { cargar(); }, [cargar]);

    const accion = async (clave, fn, exito) => {
        setOcupado(clave);
        try {
            const r = await fn();
            if (exito) showToast(exito, 'success');
            return r;
        } catch (err) {
            showToast(err.message || 'No se pudo completar.', 'error');
            return null;
        } finally {
            setOcupado('');
        }
    };

    if (!estado) {
        return (
            <div className="p-6 md:p-8 max-w-4xl mx-auto">
                <div className="h-8 w-64 bg-slate-200 rounded-lg animate-pulse mb-6"></div>
                <div className="h-96 bg-white border border-slate-200 rounded-[2rem] animate-pulse"></div>
            </div>
        );
    }

    const cabecera = (
        <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
                <h1 className="text-3xl font-black text-slate-900 tracking-tight">VeriFactu</h1>
                <p className="text-sm font-semibold text-slate-500 mt-1">Envío automático de las facturas a Hacienda.</p>
            </div>
            <button onClick={() => navigate('/facturacion')} className={botonSecundario}>Volver a facturación</button>
        </div>
    );

    // ------------------------- Plan Esencial -------------------------
    if (estado.plan !== 'completo') {
        return (
            <div className="p-6 md:p-8 space-y-6 max-w-4xl mx-auto">
                {cabecera}
                <Aviso tono="amber">
                    Tu plan <span className="font-black">Esencial</span> crea documentos provisionales, que no son facturas y no se envían a Hacienda.
                    Para emitir facturas con VeriFactu necesitas el plan <span className="font-black">Completo</span>.
                </Aviso>
                <a href={URL_PLANES} target="_blank" rel="noreferrer" className={botonPrincipal}>Pedir el plan Completo</a>
            </div>
        );
    }

    const activo = estado.estado === 'activo';
    const fiscalesOk = Boolean(estado.datosFiscales?.completos);
    const datosGuardados = Boolean(estado.tipoContribuyente && estado.territorio);
    const foral = estado.territorio === 'foral';
    const pruebaOk = estado.ultimaPrueba?.estado === 'correcta';
    const repOk = !estado.representacionNecesaria || /acept|aprob|valid|firmad|complet/.test(estado.representacion);
    const listoParaActivar = fiscalesOk && datosGuardados && !foral && estado.condicionesAceptadas && pruebaOk && repOk;
    const pruebas = estado.entorno === 'test';

    const guardarDatos = () => accion('datos', async () => aplicar(await api.put('/verifactu/datos', datos)), 'Datos guardados.');
    const aceptar = () => accion('condiciones', async () => aplicar(await api.put('/verifactu/datos', { aceptarCondiciones: true })), 'Autorización registrada.');
    const enviarPrueba = () => accion('prueba', async () => {
        const r = await api.post('/verifactu/prueba');
        setPrueba(r);
        await cargar();
    }, 'Factura de prueba enviada.');
    const descargarRepresentacion = () => accion('rep', async () => {
        const r = await api.post('/verifactu/representacion', rep, { responseType: 'blob' });
        const url = URL.createObjectURL(new Blob([r?.data || r], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = url; a.download = 'representacion-verifactu.pdf';
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 60000);
        await cargar();
    }, 'Documento descargado. Fírmalo y súbelo.');
    const subirFirmado = (archivo) => archivo && accion('subir', async () =>
        aplicar(await api.post('/verifactu/representacion/firmada', archivo, { headers: { 'Content-Type': 'application/pdf' } })),
    'Documento enviado a Verifacti.');
    const comprobarRep = () => accion('comprobar', async () => aplicar(await api.get('/verifactu/representacion')));
    const activar = () => accion('activar', async () => aplicar(await api.post('/verifactu/activar', { confirmar: true })), 'VeriFactu activado.');

    const campoRep = (campo, etiqueta) => (
        <label className="block">
            <span className="block mb-1 text-xs font-bold text-slate-500">{etiqueta}</span>
            <input className={inputBase} value={rep[campo]} onChange={(e) => setRep(r => ({ ...r, [campo]: e.target.value }))} />
        </label>
    );

    return (
        <div className="p-6 md:p-8 space-y-6 max-w-4xl mx-auto animate-fade-in">
            {cabecera}

            {/* Estado */}
            {activo ? (
                <Aviso tono="emerald">
                    <span className="font-black">VeriFactu está activado</span> desde el {fecha(estado.activadoEn)}. Todas las facturas se envían a Hacienda al emitirlas.
                    {pruebas && <span className="block mt-1 text-amber-800">Entorno de pruebas: los envíos no tienen validez fiscal.</span>}
                    {estado.facturas.porRevisar > 0 && (
                        <span className="block mt-1 text-rose-800 font-black">Hay {estado.facturas.porRevisar} factura(s) con errores en Hacienda. Revísalas en Facturación.</span>
                    )}
                    {estado.facturas.enCurso > 0 && <span className="block mt-1">{estado.facturas.enCurso} envío(s) en curso.</span>}
                </Aviso>
            ) : foral ? (
                <Aviso tono="amber">En el País Vasco y Navarra no se usa VeriFactu, sino el sistema foral (TicketBAI). Klyndu todavía no lo envía: contacta con soporte.</Aviso>
            ) : estado.obligatorioYa ? (
                <Aviso tono="rose"><span className="font-black">VeriFactu ya es obligatorio para tu clínica.</span> No se pueden emitir facturas hasta activarlo.</Aviso>
            ) : (
                <Aviso>
                    VeriFactu <span className="font-black">no está activado</span>. Será obligatorio desde el <span className="font-black">{fecha(estado.obligatorioDesde)}</span>
                    {estado.tipoContribuyente ? '' : ' (o el 1 de julio de 2027 si eres autónomo)'}. Puedes activarlo antes.
                </Aviso>
            )}

            {!activo && (
                <>
                    <Paso numero={1} titulo="Datos de la clínica" hecho={fiscalesOk && datosGuardados}>
                        {estado.datosFiscales ? (
                            <p className="text-sm font-semibold text-slate-600">
                                Se enviará con <span className="font-black text-slate-800">{estado.datosFiscales.razonSocial}</span> (NIF {estado.datosFiscales.nif}).{' '}
                                <button onClick={() => navigate('/admin/datos-fiscales')} className="font-bold text-clinica-primary underline">Cambiar datos fiscales</button>
                            </p>
                        ) : (
                            <Aviso tono="rose">
                                Faltan los datos fiscales.{' '}
                                <button onClick={() => navigate('/admin/datos-fiscales')} className="font-black underline">Rellenarlos</button>
                            </Aviso>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Opcion nombre="tipo" valor="sociedad" actual={datos.tipoContribuyente} onChange={(v) => setDatos(d => ({ ...d, tipoContribuyente: v }))}
                                    titulo="Sociedad" detalle="SL, SA… Obligatorio desde el 1 de enero de 2027." />
                            <Opcion nombre="tipo" valor="autonomo" actual={datos.tipoContribuyente} onChange={(v) => setDatos(d => ({ ...d, tipoContribuyente: v }))}
                                    titulo="Autónomo" detalle="Persona física. Obligatorio desde el 1 de julio de 2027." />
                            <Opcion nombre="territorio" valor="comun" actual={datos.territorio} onChange={(v) => setDatos(d => ({ ...d, territorio: v }))}
                                    titulo="Resto de España" detalle="Hacienda estatal (AEAT)." />
                            <Opcion nombre="territorio" valor="foral" actual={datos.territorio} onChange={(v) => setDatos(d => ({ ...d, territorio: v }))}
                                    titulo="País Vasco o Navarra" detalle="Tienen su propio sistema." />
                        </div>
                        <button onClick={guardarDatos} disabled={!datos.tipoContribuyente || !datos.territorio || ocupado === 'datos'} className={botonPrincipal}>
                            Guardar
                        </button>
                    </Paso>

                    {!foral && (
                        <>
                            <Paso numero={2} titulo="Autorización" hecho={estado.condicionesAceptadas} bloqueado={!datosGuardados}>
                                <p className="text-sm font-semibold text-slate-600 leading-relaxed">
                                    Para enviar tus facturas a Hacienda, Klyndu usa a <span className="font-black">Verifacti</span>, un proveedor especializado.
                                    Al aceptar, autorizas que tus registros de facturación se envíen a la Agencia Tributaria en tu nombre a través de Verifacti.
                                </p>
                                <button type="button" onClick={() => setVerAutorizacion(true)} className="text-sm font-bold text-clinica-primary hover:underline">
                                    Leer la autorización completa
                                </button>
                                {estado.condicionesAceptadas ? (
                                    <p className="text-sm font-black text-emerald-700">Autorización aceptada.</p>
                                ) : (
                                    <>
                                        <label className="flex items-start gap-3 text-sm font-semibold text-slate-700">
                                            <input type="checkbox" checked={aceptoAutorizacion} onChange={(e) => setAceptoAutorizacion(e.target.checked)} className="mt-1" />
                                            Autorizo el envío de los registros de facturación de mi clínica a la Agencia Tributaria a través de Klyndu y Verifacti.
                                        </label>
                                        <button onClick={aceptar} disabled={!aceptoAutorizacion || ocupado === 'condiciones'} className={botonPrincipal}>Aceptar</button>
                                    </>
                                )}
                            </Paso>

                            <Paso numero={3} titulo="Factura de prueba" hecho={pruebaOk} bloqueado={!estado.condicionesAceptadas || !fiscalesOk}>
                                <p className="text-sm font-semibold text-slate-600">
                                    Envía una factura de 1 € al <span className="font-black">entorno de pruebas</span> de Hacienda. No tiene ningún efecto real: sirve para comprobar que todo funciona.
                                </p>
                                <button onClick={enviarPrueba} disabled={ocupado === 'prueba'} className={botonPrincipal}>
                                    {ocupado === 'prueba' ? 'Enviando…' : 'Enviar factura de prueba'}
                                </button>
                                {prueba?.qr && (
                                    <div className="flex items-center gap-4 p-4 border border-slate-200 rounded-2xl">
                                        <img src={prueba.qr} alt="Código QR de la factura de prueba" className="w-28 h-28" />
                                        <div className="text-sm font-semibold text-slate-600">
                                            <p className="font-black text-slate-800">Funciona. Así se verá el QR en tus facturas.</p>
                                            {prueba.url && <a href={prueba.url} target="_blank" rel="noreferrer" className="text-clinica-primary underline">Ver en la web de pruebas de Hacienda</a>}
                                        </div>
                                    </div>
                                )}
                                {!prueba && estado.ultimaPrueba && (
                                    <p className={`text-sm font-bold ${pruebaOk ? 'text-emerald-700' : 'text-rose-700'}`}>
                                        Última prueba ({fecha(estado.ultimaPrueba.fecha)}): {pruebaOk ? 'correcta' : `fallida. ${estado.ultimaPrueba.mensaje || ''}`}
                                    </p>
                                )}
                            </Paso>

                            {estado.representacionNecesaria && (
                                <Paso numero={4} titulo="Documento de representación" hecho={repOk} bloqueado={!pruebaOk}>
                                    {!estado.altaAutomatica ? (
                                        <Aviso tono="amber">Este paso estará disponible en cuanto Klyndu termine de activar la conexión con Verifacti. Te avisaremos.</Aviso>
                                    ) : (
                                        <>
                                            <p className="text-sm font-semibold text-slate-600 leading-relaxed">
                                                Hacienda exige que la clínica firme un documento que autoriza el envío en su nombre.
                                                Rellena los datos, descárgalo, fírmalo con tu certificado digital y súbelo.
                                            </p>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                <label className="block">
                                                    <span className="block mb-1 text-xs font-bold text-slate-500">Tipo de entidad</span>
                                                    <select className={inputBase} value={rep.tipo_entidad} onChange={(e) => setRep(r => ({ ...r, tipo_entidad: e.target.value }))}>
                                                        <option value="juridica">Sociedad (persona jurídica)</option>
                                                        <option value="fisica">Autónomo (persona física)</option>
                                                    </select>
                                                </label>
                                                {campoRep('nombre_entidad', 'Nombre de la clínica o del titular')}
                                                {campoRep('calle_entidad', 'Calle')}
                                                {campoRep('numero_entidad', 'Número')}
                                                {campoRep('municipio_entidad', 'Municipio')}
                                                {campoRep('nombre_representante', 'Nombre de quien firma')}
                                                {campoRep('nif_representante', 'NIF o NIE de quien firma')}
                                                {campoRep('calle_representante', 'Calle de quien firma')}
                                                {campoRep('numero_representante', 'Número')}
                                                {campoRep('municipio_representante', 'Municipio de quien firma')}
                                            </div>
                                            <div className="flex flex-wrap gap-3">
                                                <button onClick={descargarRepresentacion} disabled={ocupado === 'rep'} className={botonPrincipal}>Descargar documento</button>
                                                <label className={`${botonSecundario} cursor-pointer`}>
                                                    Subir documento firmado
                                                    <input type="file" accept="application/pdf" className="hidden" onChange={(e) => subirFirmado(e.target.files?.[0])} />
                                                </label>
                                                <button onClick={comprobarRep} disabled={ocupado === 'comprobar'} className={botonSecundario}>Comprobar estado</button>
                                            </div>
                                            <p className="text-xs font-bold text-slate-500">Estado: {estado.representacion.replace(/_/g, ' ')}</p>
                                        </>
                                    )}
                                </Paso>
                            )}

                            <Paso numero={estado.representacionNecesaria ? 5 : 4} titulo="Activar VeriFactu" bloqueado={!listoParaActivar}>
                                <Aviso tono="amber">
                                    Desde que lo actives, <span className="font-black">todas las facturas que emitas se enviarán a Hacienda</span>.
                                    Dejar de usar VeriFactu solo tiene efecto al terminar el año.
                                </Aviso>
                                <label className="flex items-start gap-3 text-sm font-semibold text-slate-700">
                                    <input type="checkbox" checked={entendido} onChange={(e) => setEntendido(e.target.checked)} className="mt-1" />
                                    Lo entiendo y quiero activar VeriFactu{pruebas ? ' (entorno de pruebas)' : ''}.
                                </label>
                                <button onClick={activar} disabled={!entendido || ocupado === 'activar'} className={botonPrincipal}>Activar VeriFactu</button>
                            </Paso>
                        </>
                    )}
                </>
            )}

            <Modal open={verAutorizacion} onClose={() => setVerAutorizacion(false)} titulo="Autorización VERI*FACTU" size="lg">
                <TextoLegal id="verifactu" />
            </Modal>
        </div>
    );
};

export default AdminVerifactu;
